/**
 * PlanLimitPrompt — when the server says "your plan doesn't allow more"
 * (402 PLAN_LIMIT: users, branches, businesses) or "not in your plan"
 * (402 ADDON_REQUIRED: HR, payroll, CRM …), offer the way out: the owner
 * gets a button to Settings → Subscription (add-ons and plans); anyone
 * else is told to ask the owner. The caller's own error toast still shows
 * the server's message; this adds the next step, once per few seconds.
 */
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import useAuthStore from "@/stores/authStore";

const OWNER_ROLES = new Set(["tenant_owner", "super_admin", "admin"]);

export default function PlanLimitPrompt() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role) as string | undefined;

  useEffect(() => {
    const onBlocked = (e: Event) => {
      const code = (e as CustomEvent<{ code?: string }>).detail?.code;
      if (code !== "PLAN_LIMIT" && code !== "ADDON_REQUIRED") return;
      if (role && OWNER_ROLES.has(role)) {
        toast("Need more than your plan includes?", {
          id: "plan-limit-prompt",
          description: "Add extras or move up a plan in Settings → Subscription.",
          action: { label: "See options", onClick: () => navigate("/settings?tab=subscription") },
          duration: 8000,
        });
      } else {
        toast("This is outside your plan", {
          id: "plan-limit-prompt",
          description: "Ask the account owner to add it in Settings → Subscription.",
          duration: 6000,
        });
      }
    };
    window.addEventListener("eaze:subscription-blocked", onBlocked);
    return () => window.removeEventListener("eaze:subscription-blocked", onBlocked);
  }, [navigate, role]);

  return null;
}
